import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';

// usePaginatedQuery — URL-bound pagination + filter hook for admin list
// pages. Reads `offset` + `limit` from useSearchParams, lets the caller pass
// arbitrary additional filter params (also URL-bound by convention), and
// returns { rows, total, loading, error, refetch, setLimit, setOffset, page,
// pageCount }.
//
// API contract: the backend endpoint must accept ?limit=N&offset=N and return
// JSON of shape { <responseKey>: [...], pagination: { total, limit, offset } }
// — the same convention every admin list endpoint already uses.
//
// extraParams is a plain object of additional query keys (e.g. { lifecycle_stage:
// 'maintained' }). Keys with null/undefined/empty-string values are dropped.
//
// Page-size dropdown values are application convention (25/50/100); the hook
// accepts anything in DEFAULT_PAGE_SIZES via setLimit, then writes it to
// the URL so deep-links preserve user choice.

export const DEFAULT_PAGE_SIZES = [25, 50, 100];
export const DEFAULT_LIMIT = 50;

export default function usePaginatedQuery({
  path,
  responseKey = 'rows',
  extraParams = {},
  defaultLimit = DEFAULT_LIMIT,
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const limit = Number.parseInt(searchParams.get('limit') || String(defaultLimit), 10) || defaultLimit;
  const offset = Number.parseInt(searchParams.get('offset') || '0', 10) || 0;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Stable serialization of extraParams for the effect dep array.
  const extraSerialized = useMemo(() => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(extraParams)) {
      if (v == null || v === '') continue;
      sp.set(k, String(v));
    }
    return sp.toString();
  }, [extraParams]);

  const buildUrl = useCallback(() => {
    const sp = new URLSearchParams();
    sp.set('limit', String(limit));
    if (offset) sp.set('offset', String(offset));
    for (const [k, v] of Object.entries(extraParams)) {
      if (v == null || v === '') continue;
      sp.set(k, String(v));
    }
    return `${path}?${sp.toString()}`;
  }, [path, limit, offset, extraParams]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(buildUrl());
      setRows(res?.[responseKey] || []);
      setTotal(res?.pagination?.total ?? (res?.[responseKey] || []).length);
    } catch (err) {
      setError(err?.message || 'Failed to load');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
    // buildUrl already encodes path + limit + offset + extraSerialized into a string.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, limit, offset, extraSerialized, responseKey]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const updateParams = useCallback((patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v == null || v === '') next.delete(k);
        else next.set(k, String(v));
      }
      return next;
    });
  }, [setSearchParams]);

  const setLimit = useCallback((newLimit) => {
    updateParams({
      limit: newLimit === DEFAULT_LIMIT ? null : newLimit,
      offset: null,
    });
  }, [updateParams]);

  const setOffset = useCallback((newOffset) => {
    updateParams({ offset: newOffset === 0 ? null : newOffset });
  }, [updateParams]);

  const pageCount = Math.max(Math.ceil(total / limit), 1);
  const page = Math.min(Math.floor(offset / limit), pageCount - 1);

  return {
    rows, total, loading, error, refetch: fetchPage,
    limit, offset, page, pageCount,
    setLimit, setOffset,
  };
}
